import { useMutation, useQueryClient } from "react-query";
import { AccessFunctionalityTypeEnum } from "../../common/enums/AccessFunctionalityTypeEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { AccessPermission } from "../../common/models/AccessPermission";
import { RestError } from "../../services/rest-service/RestService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { MutationContextType } from "./Common";
import { keys } from "./Keys";
import ObjectTagValuesDataService from "../../services/object-tag-values-data-service/ObjectTagValuesDataService";
import { ObjectTagValuesConfiguration, ObjectTagValuesConfigurationDescription, ObjectTagValuesConfigurations } from "../../services/object-tag-values-data-service/ConfigurationModels";

export type UseSaveObjectTagValuesConfigurationParameters = {
    groupID: number,
    objectType: eLynxObjectTypeEnum,
    objectID: number,
} & BaseUseMutationParameters<ObjectTagValuesConfiguration, ObjectTagValuesConfiguration>;

export function useSaveObjectTagValuesConfiguration({
    groupID,
    objectType,
    objectID,
    onError,
    onSuccess
}: UseSaveObjectTagValuesConfigurationParameters) {
    const queryClient = useQueryClient();

    return useMutation<ObjectTagValuesConfiguration, RestError, ObjectTagValuesConfiguration, MutationContextType>(
        (configuration) => {
            return ObjectTagValuesDataService.saveConfiguration(
                groupID,
                configuration
            );
        },
        {
            onMutate: async (configuration: ObjectTagValuesConfiguration) => {

                await queryClient.cancelQueries(keys.all);

                const previousConfigurations = queryClient.getQueryData<ObjectTagValuesConfigurations>(
                    keys.configurationList(
                        groupID,
                        objectType,
                        objectID
                    )
                );
                queryClient.setQueryData<ObjectTagValuesConfigurations>(
                    keys.configurationList(
                        groupID,
                        objectType,
                        objectID
                    ),
                    (old?: ObjectTagValuesConfigurations) => {

                        const configurations = old?.configurations;

                        const existingConfiguration = configurations?.find(cfg => cfg.id === configuration.id);

                        const newModel: ObjectTagValuesConfigurationDescription = {
                            id: configuration.id,
                            name: configuration.name,
                            viewType: configuration.viewType,
                            access: configuration.access,
                            users: configuration.users,
                            userPermissions: configuration.userPermissions,
                            internal: configuration.internal
                        };

                        if (old) {
                            const copy = { ...old };
                            if (existingConfiguration) {
                                copy.configurations = copy.configurations?.map(cfg => (cfg.id === configuration.id ? newModel : cfg));
                            }
                            return copy;
                        } else {

                            return {
                                defaultConfigurationID: undefined,
                                userPermissions: [new AccessPermission({
                                    functionalityType: AccessFunctionalityTypeEnum.ObjectTagValuesConfiguration,
                                    access: 65535
                                })],
                                configurations: [newModel]
                            };
                        }
                    }
                );

                return {
                    previousConfigurations
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _configuration, context) => {

                if (error && context?.previousConfigurations) {
                    queryClient.setQueryData<ObjectTagValuesConfigurations>(
                        keys.configurationList(
                            groupID,
                            objectType,
                            objectID
                        ),
                        context?.previousConfigurations
                    );
                }

                // If there was an error in saving our configuration, such as name in use,
                // then don't invalidate the queries, otherwise the configuration will
                // reload and our changes will be discarded.
                if (!error) {
                    queryClient.invalidateQueries(keys.all);
                }
            }
        }
    );
}